import React, { useContext, useEffect, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import axios from "axios";
import { FiCopy } from "react-icons/fi";
import { createCredential } from "../../../services/integration.service";
import { ToastrContext } from "../../../provider/toastrContext";
import { WorkspaceContext } from "../../../provider/workspaceContext";
import { ThemeContext } from "../../../provider/themeContext";
import CustomStyles from "../../../components/customStyles";
import { API_URL } from "../../../utilities/url";
import storage from "../../../services/storage";
import { SCOPES_GROUPED } from "../scopesCatalogue";

// Co-located device-picker helper. Uses the otokata-api {success, message, data}
// envelope shape — kept outside integration.service.jsx so the Stripe-envelope
// interceptor there isn't contaminated.
const listWorkspaceDevices = async (workspaceId) => {
  const res = await axios.post(
    `${API_URL}list-device`,
    { idt_workspace: workspaceId },
    { headers: { Authorization: `Bearer ${storage.get("access_token")}` } }
  );
  if (!res.data || res.data.success !== true) {
    throw new Error(
      (res.data && res.data.message) || "Failed to load devices"
    );
  }
  return res.data.data.map((d) => ({
    value: d.idt_device,
    label: `${d.name} (${d.msisdn})`,
  }));
};

// Convert "YYYY-MM-DDTHH:mm" (datetime-local) -> "YYYY-MM-DD HH:mm:ss".
const toBackendTs = (localValue) => {
  if (!localValue) return "";
  const replaced = localValue.replace("T", " ");
  return replaced.length === 16 ? `${replaced}:00` : replaced;
};

const CreateCredentialModal = ({ show, onClose, appPubID, onCreated }) => {
  const { showSuccess, showError, showCopy } = useContext(ToastrContext);
  const { workspace } = useContext(WorkspaceContext);
  const { theme } = useContext(ThemeContext);
  const [stage, setStage] = useState("form");
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      scopes: [],
      environment: "live",
      devices: [],
      expires_at: "",
    },
  });

  useEffect(() => {
    if (!show) return;
    setStage("form");
    setCreated(null);
    reset({ scopes: [], environment: "live", devices: [], expires_at: "" });
    if (workspace && workspace.idt_user_workspace) {
      setDevicesLoading(true);
      listWorkspaceDevices(workspace.idt_user_workspace)
        .then((opts) => setDeviceOptions(opts))
        .catch((e) => showError(e.message || "Failed to load devices"))
        .finally(() => setDevicesLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, workspace && workspace.idt_user_workspace]);

  const reallyClose = () => {
    setStage("form");
    setCreated(null);
    reset({ scopes: [], environment: "live", devices: [], expires_at: "" });
    onClose();
  };

  const onSubmit = async (data) => {
    if (!data.scopes || data.scopes.length === 0) {
      showError("Pick at least one scope");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        scopes: data.scopes.map((s) => s.value),
        environment: data.environment,
      };
      if (data.devices && data.devices.length > 0) {
        body.device_ids = data.devices.map((d) => Number(d.value));
      }
      if (data.expires_at) {
        body.ts_expires = toBackendTs(data.expires_at);
      }
      const res = await createCredential(appPubID, body);
      setCreated(res);
      setStage("secret");
      onCreated();
      showSuccess("Credential created");
    } catch (e) {
      showError((e && e.message) || "Failed to create credential");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      showCopy(`${label} copied`);
    } catch {
      showError("Copy failed");
    }
  };

  const keyId = created ? created.key_id || "" : "";
  const secret = created ? created.secret || "" : "";

  return (
    <Modal
      show={show}
      onHide={reallyClose}
      centered
      size="lg"
      backdrop={stage === "secret" ? "static" : true}
    >
      {stage === "form" ? (
        <>
          <Modal.Header closeButton>
            <Modal.Title>New Credential</Modal.Title>
          </Modal.Header>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Modal.Body>
              <div className="mb-3">
                <label className="col-form-label">
                  Scopes<span className="required">*</span>
                </label>
                <Controller
                  name="scopes"
                  control={control}
                  rules={{
                    validate: (v) =>
                      (v && v.length > 0) || "Pick at least one scope",
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      isMulti
                      options={SCOPES_GROUPED}
                      placeholder="Select scopes..."
                      styles={CustomStyles(theme)}
                      formatOptionLabel={(opt) => (
                        <div title={opt.description || ""}>
                          <div>{opt.label}</div>
                          <div className="text-muted small">{opt.value}</div>
                        </div>
                      )}
                    />
                  )}
                />
                {errors.scopes && (
                  <div className="text-danger small mt-1">
                    {errors.scopes.message}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="col-form-label">Environment</label>
                <Form.Select {...register("environment", { required: true })}>
                  <option value="live">Live</option>
                  <option value="test">Test</option>
                </Form.Select>
              </div>

              <div className="mb-3">
                <label className="col-form-label">
                  Device Allowlist{" "}
                  <span className="text-muted small">
                    (optional — leave empty for workspace-wide access)
                  </span>
                </label>
                <Controller
                  name="devices"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      isMulti
                      options={deviceOptions}
                      isLoading={devicesLoading}
                      placeholder={
                        devicesLoading
                          ? "Loading devices..."
                          : "All devices (leave empty)"
                      }
                      styles={CustomStyles(theme)}
                    />
                  )}
                />
              </div>

              <div className="mb-3">
                <label className="col-form-label">
                  Expires At{" "}
                  <span className="text-muted small">
                    (optional — leave empty for no expiry)
                  </span>
                </label>
                <input
                  type="datetime-local"
                  className="form-control"
                  {...register("expires_at")}
                />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={reallyClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? (
                  <>
                    <Spinner
                      animation="border"
                      size="sm"
                      className="me-1"
                    />
                    Creating...
                  </>
                ) : (
                  "Create"
                )}
              </Button>
            </Modal.Footer>
          </form>
        </>
      ) : (
        <>
          <Modal.Header>
            <Modal.Title>Save your credential secret</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="alert alert-warning">
              <strong>This is the only time you'll see this secret.</strong>{" "}
              Store it somewhere safe — you can't retrieve it later.
            </div>
            <div className="mb-3">
              <label className="col-form-label">Key ID</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={keyId}
                  readOnly
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => copy(keyId, "Key ID")}
                  disabled={!keyId}
                >
                  <FiCopy />
                </Button>
              </div>
            </div>
            <div className="mb-3">
              <label className="col-form-label">Secret</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control font-monospace"
                  value={secret}
                  readOnly
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => copy(secret, "Secret")}
                  disabled={!secret}
                >
                  <FiCopy />
                </Button>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={reallyClose}>
              I've saved it
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default CreateCredentialModal;
